import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Trophy, 
  Plus, 
  RefreshCw,
  Shield, 
  Clock, 
  Hash, 
  ArrowRight,
  Star,
  Medal,
  Swords,
  Crown,
  Sparkles
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBattle } from '../context/BattleContext';
import { useUser } from '../context/UserContext';
import { RankTier, RankDivision } from '../types/battle';
import toast from 'react-hot-toast';
import { leaderboardAPI } from '../utils/apiClient';
import { firebaseBattleService } from '../utils/firebaseBattle';

// Define top players for the leaderboard
interface TopPlayer {
  username: string;
  rank: {
    tier: RankTier;
    division: RankDivision;
  };
  wpm: number;
  battles: number;
  wins: number;
}

const BattleHub: React.FC = () => {
  const navigate = useNavigate();
  const { joinRoom, getUserRank, startRankedSearch, stopRankedSearch } = useBattle();
  const { state: userState } = useUser();
  const [inviteCode, setInviteCode] = useState('');
  const [selectedMode, setSelectedMode] = useState<'ranked' | 'custom'>('ranked');
  const [isSearching, setIsSearching] = useState(false);
  const [searchTimer, setSearchTimer] = useState<NodeJS.Timeout | null>(null);
  const [topPlayers, setTopPlayers] = useState<TopPlayer[]>([]);
  const [recentBattles, setRecentBattles] = useState<any[]>([]);
  const [loadingTopPlayers, setLoadingTopPlayers] = useState(true);
  const [loadingRecentBattles, setLoadingRecentBattles] = useState(true);
  
  // Get user rank from battle context
  const userRank = getUserRank();
  
  // Fetch top players (battle leaderboard)
  useEffect(() => {
    const fetchTopPlayers = async () => {
      try {
        setLoadingTopPlayers(true);
        const data = await leaderboardAPI.get('battles', 5);
        
        // Transform leaderboard data to TopPlayer format
        const players: TopPlayer[] = data.map((entry: any) => ({
          username: entry.username,
          rank: entry.rank || { tier: 'bronze', division: 'III' }, // Default rank if not present
          wpm: entry.bestWpm || entry.avgWpm || 0,
          battles: (entry.battlesWon || 0) + (entry.battlesLost || 0),
          wins: entry.battlesWon || 0
        }));
        
        setTopPlayers(players);
      } catch (error) {
        console.error('Error fetching top players:', error);
        setTopPlayers([]);
      } finally {
        setLoadingTopPlayers(false);
      }
    };
    
    fetchTopPlayers();
  }, []);
  
  // Helper function to transform battle data
  const transformBattleData = (battle: any) => {
    const winner = battle.players.find((p: any) => p.position === 1);
    const totalWpm = battle.players.reduce((sum: number, p: any) => sum + p.wpm, 0);
    const avgWpm = battle.players.length > 0 ? Math.round(totalWpm / battle.players.length) : 0;
    
    return {
      name: battle.name,
      winner: winner?.username || 'Unknown',
      participants: battle.players.length,
      mode: 'time',
      avgWpm
    };
  };
  
  // Fetch recent completed battles
  useEffect(() => {
    const fetchRecentBattles = async () => {
      try {
        setLoadingRecentBattles(true);
        const battles = await firebaseBattleService.getRecentCompletedBattles(5);
        const formattedBattles = battles.map(transformBattleData);
        setRecentBattles(formattedBattles);
      } catch (error) {
        console.error('Error fetching recent battles:', error);
        setRecentBattles([]);
      } finally {
        setLoadingRecentBattles(false);
      }
    };
    
    fetchRecentBattles();
  }, []);
  
  // Clear search timer when component unmounts
  useEffect(() => {
    return () => {
      if (searchTimer) {
        clearTimeout(searchTimer);
      }
    };
  }, [searchTimer]);
  
  // Handle ranked match search timer
  useEffect(() => {
    if (isSearching && selectedMode === 'ranked') {
      const timer = setTimeout(() => {
        // Simulate finding a ranked match
        toast.success('Ranked match found! Joining now...');
        setIsSearching(false);
        stopRankedSearch();
        navigate('/battle/room/ranked-match');
      }, 5000); // Simulate matchmaking time
      
      setSearchTimer(timer);
    }
  }, [isSearching, navigate, selectedMode, stopRankedSearch]);
  
  const handleJoinByInviteCode = () => {
    if (!inviteCode) {
      toast.error('Please enter an invite code');
      return;
    }
    
    joinRoom(inviteCode, true)
      .then(() => navigate('/battle/room'))
      .catch(error => {
        toast.error('Invalid invite code or room is full');
        console.error('Failed to join room:', error);
      });
  };
  
  const handleRankedMatch = () => {
    if (!userState.isAuthenticated) {
      toast.error('Please log in to join ranked battles');
      return;
    }
    
    setIsSearching(true);
    startRankedSearch();
    toast.success('Searching for ranked match...');
  };
  
  const cancelSearch = () => {
    setIsSearching(false);
    stopRankedSearch();
    toast.error('Search cancelled');
  };
  
  // Define rank tier order
  const rankTierOrder: RankTier[] = ['bronze', 'silver', 'gold', 'platinum', 'diamond', 'master'];
  
  // Check if a rank tier is unlocked (user has reached this tier or higher)
  const isRankUnlocked = (tier: RankTier): boolean => {
    const currentTierIndex = rankTierOrder.indexOf(userRank.tier);
    const checkTierIndex = rankTierOrder.indexOf(tier);
    return checkTierIndex <= currentTierIndex;
  };
  
  // Helper function to get rank color (brighter when unlocked)
  const getRankColor = (tier: RankTier, unlocked: boolean = true): string => {
    // Unlocked ranks get brighter, more premium colors
    switch(tier) {
      case 'bronze': return unlocked ? 'text-amber-600' : 'text-amber-700';
      case 'silver': return unlocked ? 'text-slate-300' : 'text-slate-400';
      case 'gold': return unlocked ? 'text-yellow-400' : 'text-yellow-500';
      case 'platinum': return unlocked ? 'text-blue-300' : 'text-blue-400';
      case 'diamond': return unlocked ? 'text-purple-300' : 'text-purple-400';
      case 'master': return unlocked ? 'text-red-400' : 'text-red-500';
      default: return 'text-textMuted';
    }
  };
  
  // Helper function to get rank background color (richer when unlocked)
  const getRankBgColor = (tier: RankTier, unlocked: boolean = true): string => {
    // Unlocked ranks get richer backgrounds
    switch(tier) {
      case 'bronze': return unlocked ? 'bg-amber-700/30' : 'bg-amber-700/20';
      case 'silver': return unlocked ? 'bg-slate-400/30' : 'bg-slate-400/20';
      case 'gold': return unlocked ? 'bg-yellow-500/30' : 'bg-yellow-500/20';
      case 'platinum': return unlocked ? 'bg-blue-400/30' : 'bg-blue-400/20';
      case 'diamond': return unlocked ? 'bg-purple-400/30' : 'bg-purple-400/20';
      case 'master': return unlocked ? 'bg-red-500/30' : 'bg-red-500/20';
      default: return 'bg-secondary/20';
    }
  };
  
  // Render rank icon based on tier (same icon, unlocked status affects styling)
  const renderRankIcon = (tier: RankTier) => {
    switch(tier) {
      case 'bronze': return <Medal className="w-6 h-6" />;
      case 'silver': return <Medal className="w-6 h-6" />;
      case 'gold': return <Trophy className="w-6 h-6" />;
      case 'platinum': return <Star className="w-6 h-6" />;
      case 'diamond': return <Star className="w-6 h-6" />;
      case 'master': return <Crown className="w-6 h-6" />;
      default: return <Medal className="w-6 h-6" />;
    }
  };
  
  return (
    <div className="max-w-6xl mx-auto pb-20">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-8"
      >
        <h1 className="text-4xl font-bold mb-4 flex items-center justify-center">
          <Trophy className="w-10 h-10 mr-3 text-accent" />
          TypeRush Battles
        </h1>
        <p className="text-xl text-textMuted max-w-2xl mx-auto">
          Compete with others in real-time typing battles
        </p>
      </motion.div>

      {/* Battle Mode Selection */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="bg-cardBg rounded-xl border border-secondary mb-8"
      >
        <div className="grid grid-cols-2 gap-0">
          <button
            onClick={() => setSelectedMode('ranked')}
            className={`p-4 flex flex-col items-center justify-center border-r border-secondary transition-all ${
              selectedMode === 'ranked' 
                ? 'bg-accent/10 text-accent'
                : 'hover:bg-secondary/50'
            }`}
          >
            <Star className={`w-6 h-6 mb-2 ${selectedMode === 'ranked' ? 'text-accent' : 'text-textMuted'}`} />
            <span className={`font-medium ${selectedMode === 'ranked' ? '' : 'text-textMuted'}`}>Ranked Mode</span>
          </button>
          <button
            onClick={() => setSelectedMode('custom')}
            className={`p-4 flex flex-col items-center justify-center transition-all ${
              selectedMode === 'custom' 
                ? 'bg-accent/10 text-accent'
                : 'hover:bg-secondary/50'
            }`}
          >
            <Users className={`w-6 h-6 mb-2 ${selectedMode === 'custom' ? 'text-accent' : 'text-textMuted'}`} />
            <span className={`font-medium ${selectedMode === 'custom' ? '' : 'text-textMuted'}`}>Custom Battle</span>
          </button>
        </div>

        <AnimatePresence mode="wait">
          {/* Ranked Mode Panel */}
          {selectedMode === 'ranked' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Ranked Mode Info */}
                  <div className="md:col-span-2">
                    <h2 className="text-xl font-bold mb-4 flex items-center">
                      <Star className="w-5 h-5 mr-2 text-accent" />
                      Ranked Mode
                    </h2>
                    
                    <div className="mb-6">
                      <p className="text-textMuted mb-3">
                        Compete against players of similar skill and climb the ranks! Win matches to earn points and achieve higher tiers.
                      </p>
                      
                      <div className="bg-secondary/30 p-3 rounded-lg mt-4">
                        <h4 className="font-medium mb-2 flex items-center">
                          <Sparkles className="w-4 h-4 mr-2 text-accent" />
                          Rank Tiers
                        </h4>
                        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                          {(['bronze', 'silver', 'gold', 'platinum', 'diamond', 'master'] as RankTier[]).map(tier => {
                            const unlocked = isRankUnlocked(tier);
                            const isCurrent = tier === userRank.tier;
                            
                            return (
                              <div 
                                key={tier} 
                                className={`
                                  ${getRankBgColor(tier, unlocked)} 
                                  p-2 rounded-lg text-center flex flex-col items-center
                                  transition-all duration-300
                                  ${unlocked ? 'transform hover:scale-110 shadow-lg brightness-110' : 'hover:scale-105'}
                                  ${isCurrent ? 'ring-2 ring-accent shadow-xl shadow-accent/40 scale-105' : ''}
                                `}
                              >
                                <div className={`${getRankColor(tier, unlocked)} ${unlocked ? 'drop-shadow-lg animate-pulse-slow' : ''}`}>
                                  {renderRankIcon(tier)}
                                </div>
                                <span className={`text-xs font-medium mt-1 ${getRankColor(tier, unlocked)} ${unlocked ? 'font-bold' : ''}`}>
                                  {tier.charAt(0).toUpperCase() + tier.slice(1)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    
                    {isSearching ? (
                      <div className="text-center">
                        <div className="inline-block p-3 rounded-full bg-accent/20 mb-4">
                          <RefreshCw className="w-6 h-6 text-accent animate-spin" />
                        </div>
                        <h3 className="text-lg font-medium mb-2">Searching for ranked opponent...</h3>
                        <p className="text-textMuted mb-4">Searching for opponents near {userRank.tier} {userRank.division}</p>
                        <button
                          onClick={cancelSearch}
                          className="px-4 py-2 bg-secondary hover:bg-secondary/70 text-text rounded-lg transition-all"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={handleRankedMatch}
                        className="w-full py-4 bg-accent hover:bg-yellow-500 text-primary font-semibold rounded-lg transition-all"
                      >
                        Find Ranked Match
                      </button>
                    )}
                  </div>
                  
                  {/* Current Rank Card */}
                  <div className="bg-secondary/20 p-4 rounded-lg">
                    <h3 className="text-lg font-medium mb-4">Your Current Rank</h3>
                    <div className="flex items-center mb-3">
                      <div className={`p-2 rounded-full ${getRankBgColor(userRank.tier)} mr-3`}>
                        <div className={`${getRankColor(userRank.tier)}`}>
                          {renderRankIcon(userRank.tier)}
                        </div>
                      </div>
                      <div>
                        <div className={`text-lg font-bold ${getRankColor(userRank.tier)}`}>
                          {userRank.tier.charAt(0).toUpperCase() + userRank.tier.slice(1)} {userRank.division}
                        </div>
                        <div className="text-sm text-textMuted">
                          {userRank.points} / {userRank.pointsToNext} LP
                        </div>
                      </div>
                    </div>
                    
                    {/* Rank progress bar */}
                    <div className="w-full h-2 bg-secondary/30 rounded-full overflow-hidden">
                      <div 
                        className={`h-2 ${(() => {
                          if (userRank.tier === 'bronze') return 'bg-amber-700';
                          if (userRank.tier === 'silver') return 'bg-slate-400';
                          if (userRank.tier === 'gold') return 'bg-yellow-500';
                          if (userRank.tier === 'platinum') return 'bg-blue-400';
                          if (userRank.tier === 'diamond') return 'bg-purple-400';
                          return 'bg-red-500';
                        })()}`}
                        style={{ width: `${(userRank.points / userRank.pointsToNext) * 100}%` }}
                      ></div>
                    </div>
                    
                    {/* Ranking explanation */}
                    <div className="mt-4 text-sm">
                      <p className="flex items-center text-textMuted mb-2">
                        <Trophy className="w-4 h-4 mr-1 text-yellow-500" />
                        Win matches to gain LP
                      </p>
                      <p className="flex items-center text-textMuted">
                        <Star className="w-4 h-4 mr-1 text-accent" />
                        Reach {userRank.pointsToNext} LP to rank up
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Custom Battle Panel */}
          {selectedMode === 'custom' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Create Battle */}
                  <div className="bg-secondary/20 p-4 rounded-lg">
                    <div className="p-3 w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center mb-3">
                      <Plus className="w-6 h-6 text-accent" />
                    </div>
                    <h3 className="text-lg font-bold mb-2">Create Battle Room</h3>
                    <p className="text-textMuted mb-4">
                      Create a custom room and invite friends to compete
                    </p>
                    <button
                      onClick={() => navigate('/battle/create')}
                      className="w-full py-3 bg-accent hover:bg-yellow-500 text-primary font-semibold rounded-lg transition-all"
                    >
                      Create Room
                    </button>
                  </div>
                  
                  {/* Join Private Battle */}
                  <div className="bg-secondary/20 p-4 rounded-lg">
                    <div className="p-3 w-12 h-12 rounded-lg bg-secondary/50 flex items-center justify-center mb-3">
                      <Shield className="w-6 h-6 text-blue-400" />
                    </div>
                    <h3 className="text-lg font-bold mb-2">Join Private Battle</h3>
                    <div className="relative mb-4">
                      <input
                        type="text"
                        value={inviteCode}
                        onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                        placeholder="Enter invite code"
                        className="w-full bg-secondary/30 border border-secondary rounded-lg pl-4 pr-12 py-3 focus:outline-none focus:border-accent text-text placeholder:text-textMuted/70"
                        maxLength={6}
                      />
                      <button
                        onClick={handleJoinByInviteCode}
                        className="absolute right-0 top-0 bottom-0 px-3 flex items-center justify-center text-accent hover:text-yellow-500"
                      >
                        <ArrowRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Leaderboards section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Battlers */}
        <div className="bg-cardBg rounded-xl p-6 shadow-lg border border-secondary">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <Trophy className="w-5 h-5 mr-2 text-accent" />
            Top Battlers
          </h2>
          
          <div className="space-y-3">
            {loadingTopPlayers && (
              <div className="text-center py-8 text-textMuted">Loading top battlers...</div>
            )}
            {!loadingTopPlayers && topPlayers.length === 0 && (
              <div className="text-center py-8 text-textMuted">No battle data available yet</div>
            )}
            {!loadingTopPlayers && topPlayers.length > 0 && (
              <>
                {/* Top players list */}
                {topPlayers.map((player, index) => (
              <div key={player.username} className="flex items-center justify-between bg-secondary/20 p-3 rounded-lg">
                <div className="flex items-center">
                  <div className="w-6 h-6 rounded-full bg-secondary/50 flex items-center justify-center text-xs font-bold mr-3">
                    {index + 1}
                  </div>
                  <div>
                    <div className="font-medium">{player.username}</div>
                    <div className={`text-xs ${getRankColor(player.rank.tier)}`}>
                      {player.rank.tier.charAt(0).toUpperCase() + player.rank.tier.slice(1)} {player.rank.division}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-accent">{player.wpm} WPM</div>
                  <div className="text-xs text-textMuted">
                    {player.wins}/{player.battles} Wins
                  </div>
                </div>
              </div>
            ))}
              </>
            )}
          </div>
        </div>
        
        {/* Recent Battles */}
        <div className="bg-cardBg rounded-xl p-6 shadow-lg border border-secondary">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <Swords className="w-5 h-5 mr-2 text-accent" />
            Recent Battles
          </h2>
          
          <div className="space-y-3">
            {loadingRecentBattles && (
              <div className="text-center py-8 text-textMuted">Loading recent battles...</div>
            )}
            {!loadingRecentBattles && recentBattles.length === 0 && (
              <div className="text-center py-8 text-textMuted">No recent battles yet</div>
            )}
            {!loadingRecentBattles && recentBattles.length > 0 && (
              <>
                {/* Simulated recent battles */}
                {recentBattles.map((battle) => (
              <div key={battle.name} className="bg-secondary/20 p-3 rounded-lg">
                <div className="flex justify-between mb-1">
                  <div className="font-medium">{battle.name}</div>
                  <div className="text-sm text-textMuted">
                    {battle.mode === 'time' ? 
                      <span className="flex items-center"><Clock className="w-3 h-3 mr-1" /> Time</span> : 
                      <span className="flex items-center"><Hash className="w-3 h-3 mr-1" /> Words</span>
                    }
                  </div>
                </div>
                <div className="flex justify-between text-sm">
                  <div className="text-textMuted">
                    <span className="text-accent font-medium mr-1">{battle.winner}</span> won
                  </div>
                  <div className="text-textMuted">
                    <span className="text-text">{battle.avgWpm} WPM</span> avg
                  </div>
                </div>
              </div>
            ))}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BattleHub;
